import { useEffect, useState } from "react";
import { AlertTriangle, ArrowRight, Wallet } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import DateField from "@/components/DateField";
import { formatIDR } from "@/lib/constants";

function rowProjectLabel(pc) {
  if (pc.project_type === "variation_order") return `${pc.project_name} · VO ${pc.project_variation || ""}`.trim();
  if (pc.project_type === "working_order") return `${pc.project_name} · WO ${pc.project_wo_number || ""}`.trim();
  return pc.project_name || "Untitled";
}

/** FAT / BOD marks the petty cash as transferred, picking date + source dana + destination account.
 *  Shows breakdown: pengajuan − saldo pengaju = nominal transfer aktual (bisa Rp 0
 *  bila saldo mencukupi — sejak 2026-09-23 tetap dicatat manual, auto-transfer dihapus). */
export default function PettyCashTransferDialog({
  open, pc, transferDate, transferNote, transferBankId, transferBankOptions, transferSourceKey, transferAmount,
  onDateChange, onNoteChange, onBankChange, onSourceChange, onTransferAmountChange, onCancel, onSubmit,
}) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sourceOptions, setSourceOptions] = useState({ staff_accounts: [], company_accounts: [] });
  const [sourceLoading, setSourceLoading] = useState(false);

  // Fetch transfer-preview whenever dialog opens for a new pc — shows breakdown of
  // "pengajuan − saldo pengaju = nominal akan ditransfer" per user request 08-Feb-2026.
  useEffect(() => {
    if (!open || !pc?.id) { setPreview(null); return; }
    let cancelled = false;
    setLoading(true);
    api.get(`/petty-cash/${pc.id}/transfer-preview`)
      .then(({ data }) => {
        if (cancelled) return;
        setPreview(data);
        onTransferAmountChange?.(data?.transfer_needed ?? null);
      })
      .catch(() => { if (!cancelled) { setPreview(null); onTransferAmountChange?.(null); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, pc?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setSourceLoading(true);
    api.get("/petty-cash/source-options")
      .then(({ data }) => { if (!cancelled) setSourceOptions(data || { staff_accounts: [], company_accounts: [] }); })
      .catch(() => { if (!cancelled) setSourceOptions({ staff_accounts: [], company_accounts: [] }); })
      .finally(() => { if (!cancelled) setSourceLoading(false); });
    return () => { cancelled = true; };
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="rounded-none max-w-lg" data-testid="dlg-pc-transfer">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Tandai Sudah Transfer</DialogTitle>
        </DialogHeader>
        {pc && (
          <div className="space-y-3 text-sm">
            <div>
              <div className="font-semibold text-slate-800">{pc.week_label} — {rowProjectLabel(pc)}</div>
              <div className="text-xs text-slate-500">Pengaju: <span className="font-semibold">{pc.user_name}</span></div>
            </div>

            {/* Breakdown: pengajuan − saldo = transfer needed */}
            <div className="border-2 border-[#0B1D3A] bg-gradient-to-br from-white to-slate-50 p-3" data-testid="pc-transfer-breakdown">
              {loading ? (
                <div className="text-xs text-slate-500 italic">Menghitung nominal transfer…</div>
              ) : preview ? (
                <>
                  <div className="grid grid-cols-3 gap-2 items-end text-center">
                    <div>
                      <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">Pengajuan</div>
                      <div className="tabular-nums font-bold text-slate-800 text-sm" data-testid="pc-transfer-pengajuan">{formatIDR(preview.pc_total)}</div>
                    </div>
                    <div className="text-center text-slate-400 text-xs pb-1">−</div>
                    <div>
                      <div className="text-[9px] uppercase tracking-wider text-emerald-700 font-semibold inline-flex items-center gap-1 justify-center"><Wallet size={9} /> Saldo Pengaju</div>
                      <div className="tabular-nums font-bold text-emerald-700 text-sm" data-testid="pc-transfer-saldo">{formatIDR(preview.pengaju_balance)}</div>
                    </div>
                  </div>
                  <div className="border-t border-slate-300 my-2"></div>
                  {/* 2026-09-24 (user request): nominal transfer BISA DIEDIT —
                      default = pengajuan − saldo pengaju, disesuaikan dgn nominal
                      aktual yang ditransfer. */}
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#0B1D3A] font-semibold inline-flex items-center gap-1">
                      <ArrowRight size={11} /> Nominal Transfer
                    </div>
                    <div className="mt-1 flex items-center gap-2">
                      <span className="text-sm font-bold text-slate-500">Rp</span>
                      <input
                        data-testid="pc-transfer-amount-input"
                        inputMode="numeric"
                        placeholder="0"
                        value={transferAmount != null ? Number(transferAmount).toLocaleString("id-ID") : ""}
                        onChange={(e) => {
                          const raw = e.target.value.replace(/[^\d]/g, "");
                          onTransferAmountChange?.(raw === "" ? null : Number(raw));
                        }}
                        className="flex-1 border-2 border-[#0B1D3A] px-2 py-1.5 text-right tabular-nums text-lg font-black text-[#FF5A1F] bg-white focus:outline-none focus:ring-1 focus:ring-[#FF5A1F]"
                      />
                    </div>
                    <div className="mt-1 text-[10px] text-slate-400" data-testid="pc-transfer-amount-hint">
                      Default = pengajuan dikurangi saldo pengaju ({formatIDR(preview.transfer_needed)}) — edit bila nominal aktual transfer berbeda.
                    </div>
                  </div>
                  {preview.auto_transferable && (
                    <div className="mt-2 text-[10px] bg-emerald-50 border-l-2 border-emerald-500 pl-2 py-1 text-emerald-800">
                      ℹ Saldo pengaju sudah mencukupi — nominal transfer Rp 0. Tetap isi tanggal transfer sebagai bukti pencatatan (tanpa mutasi rekening); pengaju lalu bisa langsung mengisi realisasi.
                    </div>
                  )}
                </>
              ) : (
                <div className="text-xs text-rose-600 italic">Gagal memuat preview transfer.</div>
              )}
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500">Tanggal Transfer *</label>
              <div className="mt-1">
                <DateField testid="dp-pc-transfer" value={transferDate} onChange={(v) => onDateChange(v)} />
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500">Sumber Dana (opsional)</label>
              <select
                data-testid="select-transfer-source"
                value={transferSourceKey}
                onChange={(e) => onSourceChange(e.target.value)}
                className="rounded-none mt-1 h-9 w-full border border-slate-300 bg-white px-2 text-xs"
              >
                <option value="">— pilih sumber dana —</option>
                <option value="cash">Cash</option>
                {sourceOptions.staff_accounts.length > 0 && (
                  <optgroup label="Rekening Staff">
                    {sourceOptions.staff_accounts.map((a) => (
                      <option key={`${a.user_id}-${a.account_id}`} value={`staff:${a.user_id}:${a.account_id}`}>
                        {a.user_name} — {a.bank_name} — {a.account_no}
                      </option>
                    ))}
                  </optgroup>
                )}
                {sourceOptions.company_accounts.length > 0 && (
                  <optgroup label="Rekening Perusahaan">
                    {sourceOptions.company_accounts.map((a) => (
                      <option key={a.id} value={`company:${a.id}`}>{a.bank_name} — {a.account_no}</option>
                    ))}
                  </optgroup>
                )}
              </select>
              <div className="mt-1 text-[10px] text-slate-400" data-testid="pc-transfer-source-hint">
                {sourceLoading ? "Memuat opsi sumber dana…" : "Diisi saat verifikasi transfer; pengajuan lama tanpa sumber akan tampil sebagai —."}
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500">Rekening Tujuan Transfer</label>
              <select
                data-testid="select-transfer-bank"
                value={transferBankId}
                onChange={(e) => onBankChange(e.target.value)}
                className="rounded-none mt-1 h-9 w-full border border-slate-300 bg-white px-2 text-xs"
              >
                {transferBankOptions.length === 0 ? (
                  <option value="">Personil belum menyimpan rekening</option>
                ) : (
                  transferBankOptions.map((a, i) => (
                    <option key={a.id} value={a.id}>
                      {i === 0 ? "★ " : ""}{a.bank_name} — {a.account_no}
                    </option>
                  ))
                )}
              </select>
              {pc?.bank_account?.id && pc.bank_account.id !== transferBankId && (
                <div className="mt-1 text-[10px] text-amber-700 inline-flex items-center gap-1">
                  <AlertTriangle size={10} /> Berbeda dari rekening yang dipilih pengaju saat pengajuan
                </div>
              )}
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500">Note (opsional)</label>
              <Textarea data-testid="input-pc-transfer-note" value={transferNote} onChange={(e) => onNoteChange(e.target.value)} className="rounded-none mt-1 text-sm" rows={2} />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} className="rounded-none">Batal</Button>
          <Button data-testid="btn-confirm-pc-transfer" onClick={onSubmit} className="rounded-none bg-[#0B1D3A] hover:bg-[#132a5b] text-white">Simpan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
